import { Big } from 'big.js';
import {
  TriggerPriceConditionGTELast,
  TriggerPriceConditionGTEMark,
  TriggerPriceConditionLTELast,
  TriggerPriceConditionLTEMark,
} from '@/api/spec';
import { Side } from '@/common/constants';
import { SLTPType } from '@/feature/order_entry/order_types';
import { tpSlSign, triggerPriceFromPercent } from '@/feature/terminal_tables/tp_sl_modal/fields/tp_sl_utils';
import useAppStore from '../../../store/rootStore';

/** Feature flag: trigger on mark price (GTEMark/LTEMark) instead of last (GTELast/LTELast). */
export function shouldTpSlTriggerOnMarkPrice(): boolean {
  return !!useAppStore.getState().config?.features?.tpSlTriggerOnMarkPrice;
}

/** Feature flag: submit TP/SL orders as IoC (market-like) instead of GtC (limit-like). */
export function shouldTpSlOrdersUseIoc(): boolean {
  return !!useAppStore.getState().config?.features?.tpSlOrdersUseIoc;
}

/** Picks the GTE or LTE trigger condition for the configured reference price (last vs mark). */
function triggerCondition(isGte: boolean): number {
  if (shouldTpSlTriggerOnMarkPrice()) {
    return isGte ? TriggerPriceConditionGTEMark : TriggerPriceConditionLTEMark;
  }
  return isGte ? TriggerPriceConditionGTELast : TriggerPriceConditionLTELast;
}

/**
 * Determines the trigger condition (GTE/LTE) based on position side and whether it's Take Profit or Stop Loss.
 *
 * Logic (reference price — last or mark — is selected by the tpSlTriggerOnMarkPrice flag):
 * - TP for LONG (Sell): Price >= TP (GTE)
 * - TP for SHORT (Buy): Price <= TP (LTE)
 * - SL for LONG (Sell): Price <= SL (LTE)
 * - SL for SHORT (Buy): Price >= SL (GTE)
 */
export function getTpSlTriggerCondition(positionSide: Side, isTakeProfit: boolean): number {
  const isGte = positionSide === Side.long ? isTakeProfit : !isTakeProfit;
  return triggerCondition(isGte);
}

/** Default condition for a standalone stop order: long triggers on rise (GTE), short on fall (LTE). */
export function getStopTriggerCondition(orderSide: Side): number {
  return triggerCondition(orderSide === Side.long);
}

/**
 * Calculates the absolute trigger price based on the type (Price vs Percent).
 * Percent is ROE (leverage-adjusted) and delegates to the shared `triggerPriceFromPercent` so the
 * order form and the position TP/SL modal stay in lockstep (see its definition).
 */
export function calculateTriggerPrice(
  type: SLTPType | undefined,
  value: number,
  referencePrice: Big,
  positionSide: Side,
  isTakeProfit: boolean,
  leverage: number
): Big {
  if (type === SLTPType.percent) {
    return triggerPriceFromPercent(referencePrice.toString(), value, tpSlSign(positionSide, isTakeProfit), leverage);
  }
  return new Big(value);
}
